// src/components/ProtectedRoute.js
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { canAccessTool } from '../config/roles';

// Any authenticated user.
//
// Students are routed to /my-checkout. We wait for roleResolving:false
// before making any redirect decision — during the brief login window
// where a fresh user is temporarily stamped as 'faculty' while AuthContext
// looks them up in /students vs /staff, we don't want to render the
// faculty UI for someone who's actually a student.
//
// Users with role:'rejected' (set by AuthContext when an unknown email
// can't be cleaned up via delete) are bounced to /login. They should be
// signed out by AuthContext, but if they somehow reach a protected page
// we defend against it here too.
export function ProtectedRoute({ children }) {
  const { user, loading, roleResolving, isStudent, isSuperAdmin, profile } = useAuth();
  if (loading || roleResolving) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (profile?.role === 'rejected') return <Navigate to="/login" replace />;
  if (isStudent && !isSuperAdmin) {
    return <Navigate to="/my-checkout" replace />;
  }
  return children;
}

// SuperAdmin only — checks isSuperAdmin from AuthContext
// (covers both hardcoded owner UID and Firestore-promoted superadmins).
// Students cannot reach SuperAdmin routes; they're bounced to /my-checkout.
export function SuperAdminRoute({ children }) {
  const { user, loading, roleResolving, isSuperAdmin, isStudent, profile } = useAuth();
  if (loading || roleResolving) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (profile?.role === 'rejected') return <Navigate to="/login" replace />;
  if (isStudent && !isSuperAdmin) {
    return <Navigate to="/my-checkout" replace />;
  }
  if (!isSuperAdmin) return <Navigate to="/" replace />;
  return children;
}

// Any tool that needs explicit access granted via allowedTools or default: true.
// Usage: <ToolRoute toolId="ap_explorer"><ApExplorer /></ToolRoute>
//
// Students are bounced to /my-checkout — no faculty tool is reachable.
export function ToolRoute({ toolId, children }) {
  const { user, loading, roleResolving, isSuperAdmin, isStudent, allowedTools, profile } = useAuth();
  if (loading || roleResolving) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (profile?.role === 'rejected') return <Navigate to="/login" replace />;
  if (isStudent && !isSuperAdmin) {
    return <Navigate to="/my-checkout" replace />;
  }
  if (!canAccessTool(toolId, { isSuperAdmin, allowedTools })) {
    return <Navigate to="/" replace />;
  }
  return children;
}

// Dean or SuperAdmin only. For tools scoped to leadership by ROLE (not by
// per-tool allowedTools grants) — e.g. the Gradebook Audit. Students are
// bounced to /my-checkout; plain faculty are sent home.
export function DeanRoute({ children }) {
  const { user, loading, roleResolving, isSuperAdmin, isDean, isStudent, profile } = useAuth();
  if (loading || roleResolving) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (profile?.role === 'rejected') return <Navigate to="/login" replace />;
  if (isStudent && !isSuperAdmin) return <Navigate to="/my-checkout" replace />;
  if (!isDean && !isSuperAdmin) return <Navigate to="/" replace />;
  return children;
}